import React, { useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { Redirect, useParams, useLocation } from 'react-router-dom'
import { wpApi } from 'api'
import * as actions from 'member-actions'
import { selectors } from 'member-reducer'
import { selectors as membershipsSelector } from 'memberships/reducer'
import { ExpiredMembershipWarningModal } from 'member-portal/components'
import { SiteMetadata } from 'components'
import { Spinner } from 'lp-components'
import { isEmpty } from 'lodash'
import { AllMembershipsType, MEMBERSHIP_STATUS, VideoDetailsType } from 'types'
import {
  makeMobileAppPath,
  makePath,
  makeMembershipsPath,
  PATH,
} from 'config'
import { getPrimaryMembership, parseQuery } from 'utils'

const useContentDetails = (
  blogID,
  blogDetails,
  videoID,
  videoDetails,
  isVideo,
  setBlogDetails,
  setVideoDetails,
) => {
  return useMemo(
    () => ({
      contentType: isVideo ? 'videos' : 'posts',
      contentId: videoID || blogID,
      contentDetails: isVideo ? videoDetails : blogDetails,
      redirectPath: isVideo ? PATH.VIDEOS : PATH.BLOG,
      setContentDetails: isVideo ? setVideoDetails : setBlogDetails,
    }),
    [
      blogID,
      blogDetails,
      videoID,
      videoDetails,
      isVideo,
      setBlogDetails,
      setVideoDetails,
    ],
  )
}

const propTypes = {
  allMemberships: AllMembershipsType,
  blogDetails: PropTypes.object,
  children: PropTypes.node,
  setBlogDetails: PropTypes.func,
  setVideoDetails: PropTypes.func,
  videoDetails: VideoDetailsType,
}

function GatedContentLayout({
  allMemberships = {},
  blogDetails = {},
  children,
  setBlogDetails,
  setVideoDetails,
  videoDetails = {},
}) {
  const isOpen = useRef(false)
  const { videoID, blogID } = useParams()
  const { search } = useLocation()
  const { sid } = parseQuery(search)

  const isMobileApp = !!sid
  const isVideo = !!videoID
  const { membershipStatus, sfid } = getPrimaryMembership(allMemberships)

  const {
    contentType,
    contentId,
    contentDetails,
    redirectPath,
    setContentDetails,
  } = useContentDetails(
    blogID,
    blogDetails,
    videoID,
    videoDetails,
    isVideo,
    setBlogDetails,
    setVideoDetails,
  )

  useEffect(() => {
    if (contentId && isEmpty(contentDetails)) {
      const getContentDetails = async () => {
        const contentDetails = await wpApi(`${contentType}/${contentId}`)
        setContentDetails(contentDetails)
      }

      getContentDetails()
    }
  }, [contentId, contentDetails, contentType, setContentDetails, sid])

  if (!contentDetails.id) return <Spinner />

  const { id, is_private } = contentDetails

  const handleCancel = () => {
    window.location.replace(makePath(PATH.SEATOW, redirectPath))
  }

  // First validate if the user is coming from a mobile app and if the
  // content is a video. If so, redirect to the mobile app video page.
  if (isMobileApp && isVideo) {
    const mobileAppVideoPath = `${makeMobileAppPath(PATH.VIDEOS, `/${videoID}`)}?sid=${sid}`

    return <Redirect to={mobileAppVideoPath} />
  }

  // If the blog/video is private, then first redirect and authenticate the
  // user. If the member is expired, persist the Expired Membership modal. If
  // the user is on a Do Not Renew or Cancelled status, redirect them back to
  // Seatow.com. Otherwise, render the gated content.
  if (is_private) {
    if (!membershipStatus) {
      return (
        <Redirect
          to={makeMembershipsPath(PATH.GATED_CONTENT, redirectPath, `/${id}`)}
        />
      )
    }

    if (membershipStatus === MEMBERSHIP_STATUS.EXPIRED && !isOpen.current) {
      isOpen.current = true
    }

    if (
      [MEMBERSHIP_STATUS.DO_NOT_RENEW, MEMBERSHIP_STATUS.CANCELLED].includes(
        membershipStatus,
      )
    ) {
      window.location.replace(PATH.SEATOW)
      return null
    }
  }

  return (
    <>
      {!is_private && isVideo && (
        <SiteMetadata
          metaTitle={contentDetails.title}
          description={contentDetails.short_description}
        />
      )}
      <ExpiredMembershipWarningModal
        handleCancel={handleCancel}
        isOpen={isOpen.current}
        sfid={sfid}
      />
      <div className={`gated-content ${contentType}`}>{children}</div>
    </>
  )
}

GatedContentLayout.propTypes = propTypes

function mapStateToProps(state) {
  return {
    allMemberships: membershipsSelector.allMemberships(state),
    blogDetails: selectors.blogDetails(state),
    videoDetails: selectors.videoDetails(state),
  }
}

const mapDispatchToProps = {
  setBlogDetails: actions.setBlogDetails,
  setVideoDetails: actions.setVideoDetails,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  GatedContentLayout,
)
